import { useTranslation } from "react-i18next";
import SectionHeading from "./SectionHeading";
import { HeadingVariant } from "../types/constants";
import { useQuery } from "@tanstack/react-query";
import { contactsServiceFactory } from "../services/ContactsServiceImpl";
import { managementServiceFactory } from "../services/ManagementServiceImpl";

const contactsService = contactsServiceFactory();
const managementService = managementServiceFactory();

export default function Contact() {
    const { t, i18n } = useTranslation();

    const { data: contacts } = useQuery({
        queryKey: ["contacts", i18n.language],
        queryFn: async () => await contactsService.getContacts(),
    });

    const { data: managementData } = useQuery({
        queryKey: ["management", 1, i18n.language],
        queryFn: async () => {
            const { rows } = await managementService.getAll(1);
            return rows;
        },
        enabled: !!contacts?.directorId,
    });

    const director = contacts?.directorId
        ? managementData?.find((m) => m.id === contacts.directorId)
        : null;
    const directorName = director?.fullName || contacts?.directorId || "";

    return (
        <section className="relative py-20 md:py-24 lg:py-28">
            <div className="absolute top-0 left-1/2 -translate-x-1/2 w-screen h-full bg-background-400 -z-10" />
            <div className="max-w-[1280px] mx-auto flex flex-col lap:flex-row gap-12 xl:gap-[167px]">
                <div className="w-full lg:w-[487px] xl:w-[630px] flex flex-col gap-8">
                    <div className="flex flex-col gap-6">
                        <div className="w-[176px] h-[40px]">
                            <SectionHeading text={t("home.headings.contacts")} variant={HeadingVariant.LIGHT} />
                        </div>
                        <p className="text-xl text-text-400">{t("home.subheadings.contacts")}</p>
                    </div>

                    {contacts ? (
                        <div className="flex flex-col gap-8">
                            <div className="flex flex-col gap-3">
                                <p className="text-lg text-text-400">{t("home.contacts.addressLabel")}</p>
                                <p className="text-lg md:text-[22px] text-text-400">{contacts.address}</p>
                            </div>

                            <div className="flex flex-col md:flex-row gap-8">
                                <div className="flex flex-col gap-3">
                                    <p className="text-lg text-text-400">{t("home.contacts.phoneLabel")}</p>
                                    <p className="text-lg md:text-[22px] text-text-400">{contacts.phone}</p>
                                </div>

                                <div className="flex flex-col gap-3">
                                    <p className="text-lg text-text-400">{t("home.contacts.emailLabel")}</p>
                                    <p className="text-lg md:text-[22px] text-text-400 break-words">{contacts.email}</p>
                                </div>
                            </div>

                            {directorName && (
                                <div className="flex flex-col gap-3">
                                    <p className="text-lg text-text-400">{t("home.contacts.directorLabel")}</p>
                                    <p className="text-lg md:text-[22px] text-text-400">{directorName}</p>
                                </div>
                            )}
                        </div>
                    ) : (
                        <div className="flex flex-col gap-8">
                            <div className="flex flex-col gap-3">
                                <p className="text-lg text-text-400">{t("home.contacts.addressLabel")}</p>
                                <p className="text-lg md:text-[22px] text-text-400">{t("home.contacts.addressText")}</p>
                            </div>

                            <div className="flex flex-col md:flex-row gap-8">
                                <div className="flex flex-col gap-3">
                                    <p className="text-lg text-text-400">{t("home.contacts.phoneLabel")}</p>
                                    <p className="text-lg md:text-[22px] text-text-400">{t("home.contacts.phoneText")}</p>
                                </div>

                                <div className="flex flex-col gap-3">
                                    <p className="text-lg text-text-400">{t("home.contacts.emailLabel")}</p>
                                    <p className="text-lg md:text-[22px] text-text-400 break-words">
                                        {t("home.contacts.emailText")}
                                    </p>
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* <div className="rounded-[6px] w-full lg:w-[629px] h-[300px] md:h-[379px] bg-[#e0e0e0]" /> */}   
            </div>
        </section>
    );
}
